/**
 * ScrollFade — bottom-edge gradient affordance that hints "more below".
 *
 * Two flavors:
 *   - `<ScrollFadeOverlay>` + `useScrollFadeBottom(ref)` for scrollable
 *     containers (typically modals with `overflow: auto`). Place the
 *     overlay as the last child of the scroll container; its
 *     `position: sticky, bottom: 0` keeps it pinned to the visible
 *     bottom while content scrolls behind it. Marked `pointer-events:
 *     none` so it never eats taps.
 *   - `<WindowScrollFadeBottom>` for body-scrolled pages — mount once
 *     at the app root, listens to window scroll. `position: fixed`
 *     overlay at the viewport bottom. Fades out smoothly when the user
 *     scrolls all the way to the end (or when content fits without scroll).
 *
 * Visibility transitions via opacity (180ms ease) so the affordance
 * doesn't pop on/off jarringly when scroll position changes.
 *
 * Why the gradient at all: on mobile (especially iOS) the platform
 * scrollbar is overlay-only and auto-hides, so users can't tell at a
 * glance whether more content sits below the fold. The fade is a
 * subtle "yep, keep scrolling" hint that doesn't add chrome cost.
 */
import React, { useEffect, useState } from "react";
import { useIsTouchDevice } from "../hooks/useIsTouchDevice";

// Distance (px) below the viewport at which the fade is at full strength.
// Once the user can scroll fewer than this many pixels, the fade ramps
// linearly to zero so it visually "rolls out" rather than snapping off.
export const FADE_RAMP_PX = 80;

/**
 * Tracks how much more content sits below the current scroll position,
 * normalized to 0..1. Re-evaluates on scroll, resize, and DOM mutations
 * (so dynamically added content updates the value without manual triggers).
 *
 * @returns {number} 1 when there's plenty more below, 0 at the bottom or
 *                   when the content fits without scrolling. Linear ramp
 *                   over the last FADE_RAMP_PX pixels of scroll.
 */
export function useScrollFadeBottom(ref) {
  const [intensity, setIntensity] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!ref.current) return;
        const more = ref.current.scrollHeight - ref.current.scrollTop - ref.current.clientHeight;
        const next = Math.max(0, Math.min(1, more / FADE_RAMP_PX));
        // Skip noisy sub-pixel updates — opacity ≤0.01 deltas aren't
        // visually distinguishable and re-render churn isn't worth it.
        setIntensity(prev => Math.abs(prev - next) > 0.01 ? next : prev);
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", update);
      ro.disconnect();
      mo.disconnect();
    };
  }, [ref]);
  return intensity;
}

/**
 * Tracks how much more content sits below the current viewport, normalized
 * to 0..1. Used by the global `<WindowScrollFadeBottom>`. Returns 0 while
 * any `[role="dialog"][aria-modal="true"]` is mounted — the body scroll
 * is locked and visually overlaid by the modal backdrop, so the fade would
 * just look like an out-of-place light strip at the bottom of the dimmed
 * page.
 */
export function useWindowScrollFadeBottom() {
  const [intensity, setIntensity] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const docH = document.documentElement.scrollHeight;
        const more = docH - window.scrollY - window.innerHeight;
        const next = Math.max(0, Math.min(1, more / FADE_RAMP_PX));
        setIntensity(prev => Math.abs(prev - next) > 0.01 ? next : prev);
      });
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, []);
  useEffect(() => {
    const checkModal = () => {
      const open = document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
      setModalOpen(prev => prev !== open ? open : prev);
    };
    checkModal();
    const mo = new MutationObserver(checkModal);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
  return modalOpen ? 0 : intensity;
}

/**
 * Sticky bottom-fade overlay. Intended as the LAST child of a scrollable
 * container (a flex/block parent with `overflow: auto`).
 *
 * The overlay's opacity is driven by the `visible` prop, expected to be
 * the 0..1 intensity returned by `useScrollFadeBottom` (kept as a
 * loose-typed `visible` for backward symmetry; passing a boolean works
 * too — it just gives a hard on/off). Gradient ends at `color` with the
 * last 20% solid so the affordance reads clearly even on busy content.
 *
 * @param {number|boolean} visible    - 0..1 intensity (or boolean for hard toggle)
 * @param {string}         color      - gradient end color (matches modal bg)
 * @param {number}         [height=56]
 * @param {number}         [borderRadius=0] - matches the parent's bottom corner radius
 */
export function ScrollFadeOverlay({ visible, color, height = 56, borderRadius = 0 }) {
  const opacity = typeof visible === "number" ? visible : (visible ? 1 : 0);
  return (
    <div
      aria-hidden="true"
      style={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        height,
        // Pulled up so the gradient overlays the bottom of the body
        // content instead of sitting in extra blank space below it.
        marginTop: -height,
        pointerEvents: "none",
        background: `linear-gradient(to bottom, transparent 0%, ${color} 80%)`,
        opacity,
        // Keep the transition short so the proportional opacity from
        // scroll updates doesn't lag visibly; long enough that the
        // boolean modal-open flip still feels smooth.
        transition: "opacity 0.1s linear",
        borderBottomLeftRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
        zIndex: 2,
      }}
    />
  );
}

/**
 * Global fixed-position fade overlay for window/body scroll. Mount once
 * at the app root; reads window scroll directly so it covers any tab /
 * route without per-component wiring. The overlay sits BELOW page chrome
 * (sticky header z-index 50, MapTab FAB z-index 60) and BELOW any modal
 * (z-index 100+) so it's purely a visual hint and never overlaps
 * interactive elements.
 *
 * Positioning: anchored with plain `position: fixed; bottom: 0`. The only
 * two configurations that render this overlay are desktop browsers and
 * installed PWAs (standalone) — see the suppression in the component below.
 * NEITHER has a collapsing URL bar, so there is nothing to chase: the layout
 * viewport bottom IS the visible bottom. (Mobile browser tabs, where the iOS
 * Safari URL-bar collapse made `position: fixed` unreliable, are suppressed
 * entirely rather than approximated.) The fade is additionally hidden while
 * the software keyboard is open so it can never strand mid-screen above the
 * keyboard — the bug a visual-viewport `top` calc used to cause in PWAs.
 *
 * @param {string} color    - gradient end color (matches body background)
 * @param {number} [height=56]
 */
/**
 * True while the on-screen keyboard is (likely) open — detected as the
 * visual viewport being meaningfully shorter than the layout viewport
 * (`window.innerHeight - visualViewport.height > 150px`).
 *
 * Why the body-scroll fade needs this: in an installed PWA the fade is
 * anchored with `position: fixed; bottom: 0`. iOS does NOT shrink the
 * layout viewport when the keyboard opens (only the visual viewport), so
 * the fixed overlay stays at the true window bottom, hidden behind the
 * keyboard — fine. But on platforms / modes where the keyboard DOES resize
 * the layout viewport, a bottom-anchored overlay would surface stranded
 * just above the keyboard (≈ middle of the screen). Zeroing its opacity
 * while the keyboard is open guarantees the fade never appears mid-screen
 * regardless of platform. Returns false when there's no visualViewport API
 * (desktop), where there is no software keyboard to worry about.
 */
function useKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const hidden = window.innerHeight - vv.height;
        const next = hidden > 150;
        setOpen(prev => prev !== next ? next : prev);
      });
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return open;
}

/* `useIsTouchDevice` (hooks/useIsTouchDevice.js) is what suppresses the
 * body-scroll fade on mobile *browser tabs*, where iOS Safari's URL bar
 * collapse animation makes `position: fixed` unreliable enough that a partial
 * fade looks broken — better to skip it than ship a half-working version. In an
 * installed PWA there is no URL bar, so the fade is reliable and stays on (see
 * `WindowScrollFadeBottom`). The modal-scoped `<ScrollFadeOverlay>` is
 * unaffected either way: it uses `position: sticky` inside scrollable
 * containers, so no URL bar issue. */

/**
 * True when running as an installed PWA (standalone display mode, or iOS's
 * legacy `navigator.standalone`). Mirrors the detection used in
 * `firebase.js` / `useAuth.js`. The body-scroll fade re-enables on touch
 * devices in this mode because a standalone window has no collapsing URL
 * bar — the exact thing that made `position: fixed` unreliable in Safari
 * tabs no longer applies.
 */
function useIsStandalone() {
  const [standalone, setStandalone] = useState(() => {
    if (typeof window === "undefined") return false;
    return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
      || window.navigator.standalone === true;
  });
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia("(display-mode: standalone)");
    const handler = (e) => setStandalone(e.matches || window.navigator.standalone === true);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);
  return standalone;
}

// ── Bottom chrome that covers the viewport bottom ──
// A FullPageEditor whose sticky footer is stuck (content continues behind it)
// draws its own fade above that footer, inside the card. The window fade would
// sit ON the footer (z 40 vs the footer's 5) and darken its buttons, so it is
// off while any frame reports itself here. Module-level on purpose: the two
// sit in unrelated subtrees, and a context would need a provider above both.
const bottomChromeIds = new Set();
const bottomChromeListeners = new Set();

/** A frame reports (`on`) or withdraws its stuck footer under a stable `id`. */
export function setBottomChromeCovering(id, on) {
  const had = bottomChromeIds.has(id);
  if (on === had) return;
  if (on) bottomChromeIds.add(id);
  else bottomChromeIds.delete(id);
  for (const fn of bottomChromeListeners) fn(bottomChromeIds.size > 0);
}

function useBottomChromeCovering() {
  const [covering, setCovering] = useState(() => bottomChromeIds.size > 0);
  useEffect(() => {
    bottomChromeListeners.add(setCovering);
    setCovering(bottomChromeIds.size > 0);
    return () => { bottomChromeListeners.delete(setCovering); };
  }, []);
  return covering;
}

export function WindowScrollFadeBottom({ color, height = 56 }) {
  const isTouch = useIsTouchDevice();
  const isStandalone = useIsStandalone();
  const intensity = useWindowScrollFadeBottom();
  const keyboardOpen = useKeyboardOpen();
  const chromeCovering = useBottomChromeCovering();
  // Suppress only on touch devices in a browser tab — there the iOS Safari
  // URL-bar collapse breaks fixed positioning. In an installed PWA there is
  // no URL bar (the visual viewport is stable), so the affordance works and
  // stays on. Desktop (hover-capable) keeps it everywhere.
  if (isTouch && !isStandalone) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        // Plain bottom-anchor: the only renderers (desktop + standalone PWA)
        // have no collapsing URL bar, so the layout viewport bottom is the
        // visible bottom. No visual-viewport `top` calc — that chased the
        // shrinking viewport and stranded the fade mid-screen when the PWA
        // keyboard opened.
        bottom: 0,
        height,
        pointerEvents: "none",
        background: `linear-gradient(to bottom, transparent 0%, ${color} 80%)`,
        // Hide while the keyboard is open so the fade never appears stranded
        // above it; otherwise driven by how much content sits below.
        // A stuck card footer draws its own fade (see setBottomChromeCovering).
        opacity: keyboardOpen || chromeCovering ? 0 : intensity,
        transition: "opacity 0.1s linear",
        zIndex: 40,
      }}
    />
  );
}
